import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  getPublicCollectionOverview,
  getPublicCollections,
  getPublicCollectionsSource,
  publicCollectionCopy,
} from "../services/use-public-collection.service";
import { toast } from "react-toastify";

const useCollection = (filter, setLoading) => {
  const [publicCollections, setPublicCollections] = useState([]);
  const [publicCollectionsSource, setPublicCollectionsSource] = useState([]);

  const [filteredPublicCollections, setFilteredPublicCollection] = useState([]);

  const abortControllerRef = useRef(null);
  const hasFetchedRef = useRef(false);
  const { user } = useSelector((state) => state.auth);
  const params = useParams();
  const navigate = useNavigate();

  const getPublicCollection = async () => {
    try {
      setLoading?.(true);
      const data = await getPublicCollections(user, "");
      setPublicCollections(data);
      return data;
    } catch (e) {
      console.error(e);
    } finally {
      setLoading?.(false);
    }
  };

  const fetchPublicCollectionsSource = async (id) => {
    if (!id) return;
    try {
      setLoading?.(true);
      const data = await getPublicCollectionsSource(user, id);
      setPublicCollectionsSource(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading?.(false);
    }
  };

  const handlePublicCollectionOverview = async (id) => {
    try {
      const data = getPublicCollectionOverview(user, id);
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  const handlePublicCollectionCopy = async (user, id) => {
    try {
      const private_id = await publicCollectionCopy(user, id);
      toast.success("Public Collection copied successfully!");
      navigate(`/collections/${private_id}`);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (user && !filter && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      getPublicCollection();
    }
  }, [user, params.id]);

  useEffect(() => {
    if (!filter) {
      setFilteredPublicCollection([]);
      if (publicCollections.length > 0) {
        setLoading?.(false);
      }
      return;
    }
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const fetchFiltered = async () => {
      setLoading?.(true);
      try {
        const data = await getPublicCollections(user, filter, signal);
        if (!signal.aborted) {
          setFilteredPublicCollection(data);
        }
        return data;
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error(e);
        }
      } finally {
        if (!signal.aborted) {
          setLoading?.(false);
        }
      }
    };

    fetchFiltered();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [filter]);

  return {
    publicCollections,
    fetchPublicCollectionsSource,
    publicCollectionsSource,
    handlePublicCollectionOverview,
    filteredPublicCollections,
    setPublicCollectionsSource,
    handlePublicCollectionCopy,
  };
};

export default useCollection;
